import React, { useCallback, useEffect, useState } from 'react';
import { buildContentVideoUrl } from '../../config';
import BrandLogo from '../BrandLogo';
import { BackIcon, getAdminSessionInfo, LogoutIcon } from '../AdminShared/helpers';
import loadingStyles from '../shared/Loading.module.css';
import styles from '../AdminTrackVideos.module.css';

export { getAdminSessionInfo };

const IMAGE_FILE_ERROR = 'Please select an image file only.';
const VIDEO_FILE_ERROR = 'Please select a video file only.';

export const getContentId = (item) => String(item?.id ?? '').trim();

export const displayText = (value, fallback = '-') => String(value ?? '').trim() || fallback;

export const asActionsArray = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item ?? '').trim()).filter(Boolean);
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map((item) => String(item ?? '').trim()).filter(Boolean);
  } catch (_error) {
    return value.split('\n').map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

export const actionsFromContent = (item) => {
  const actions = asActionsArray(item?.actions);
  return actions.length ? actions : [''];
};

export const getContentCoverUrl = (item) =>
  buildContentVideoUrl({
    filename: item?.cover_image,
  });

export const getContentVideoUrl = (item) =>
  buildContentVideoUrl({
    filename: item?.video,
  });

export const sortContents = (items) =>
  [...(Array.isArray(items) ? items : [])].sort((a, b) =>
    displayText(a?.title, '').localeCompare(displayText(b?.title, ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    }),
  );

export function useCoverPreview() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const handleChange = useCallback((event) => {
    const nextFile = event.target.files?.[0];
    if (!nextFile) {
      setFile(null);
      setPreviewUrl(null);
      return null;
    }
    if (!nextFile.type.startsWith('image/')) {
      event.target.value = '';
      setFile(null);
      setPreviewUrl(null);
      return IMAGE_FILE_ERROR;
    }
    setFile(nextFile);
    setPreviewUrl(URL.createObjectURL(nextFile));
    return null;
  }, []);

  const clear = useCallback(() => {
    setFile(null);
    setPreviewUrl(null);
  }, []);

  return { file, previewUrl, handleChange, clear };
}

export function useVideoPreview() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const handleChange = useCallback((event) => {
    const nextFile = event.target.files?.[0];
    if (!nextFile) {
      setFile(null);
      setPreviewUrl(null);
      return null;
    }
    if (!nextFile.type.startsWith('video/')) {
      event.target.value = '';
      setFile(null);
      setPreviewUrl(null);
      return VIDEO_FILE_ERROR;
    }
    setFile(nextFile);
    setPreviewUrl(URL.createObjectURL(nextFile));
    return null;
  }, []);

  return { file, previewUrl, handleChange };
}

export const ContentCoverPreview = React.memo(function ContentCoverPreview({ url, emptyText, alt }) {
  if (url) {
    return <img className={styles['upload-preview-image']} src={url} alt={alt || 'Cover preview'} />;
  }

  return (
    <div className={styles['upload-preview-empty']}>
      <span className={styles['upload-preview-empty-icon']} aria-hidden>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <circle cx="8.5" cy="9" r="1.5" />
          <path d="M21 16l-5.5-5.5L5 21" />
        </svg>
      </span>
      <p className={styles['upload-preview-empty-text']}>{emptyText}</p>
    </div>
  );
});

export const ContentVideoPreview = React.memo(function ContentVideoPreview({ url, emptyText }) {
  if (url) {
    return (
      <video className={styles['upload-preview-video']} controls preload="metadata" playsInline>
        <source src={url} type="video/mp4" />
        <source src={url} type="video/webm" />
        <source src={url} type="video/ogg" />
        Your browser does not support the video tag.
      </video>
    );
  }

  return (
    <div className={styles['upload-preview-empty']}>
      <span className={styles['upload-preview-empty-icon']} aria-hidden>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="5" width="15" height="14" rx="2" />
          <path d="M17 10l5-3v10l-5-3V10z" />
        </svg>
      </span>
      <p className={styles['upload-preview-empty-text']}>{emptyText}</p>
    </div>
  );
});

export function AdminCategoryContentsLoadingState({ message }) {
  return (
    <div className={loadingStyles['loading-container']}>
      <div className={loadingStyles['loading-spinner']}>
        <div className={loadingStyles['spinner-ring']} />
        <div className={loadingStyles['spinner-ring']} />
        <div className={loadingStyles['spinner-ring']} />
      </div>
      <p>{message}</p>
    </div>
  );
}

export function FeedbackBanner({ feedback, className }) {
  if (!feedback) return null;

  return (
    <div
      className={[styles['feedback'], styles[`feedback-${feedback.type}`], className].filter(Boolean).join(' ')}
      role={feedback.type === 'error' ? 'alert' : 'status'}
    >
      {feedback.message}
    </div>
  );
}

export const EditIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.83H5v-.92l9.06-9.06.92.92L5.92 20.08zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
    />
  </svg>
);

export const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M6 7h12v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7zm3-5h6l1 1h4v2H4V3h4l1-1zm1 8h2v10h-2V10zm4 0h2v10h-2V10z"
    />
  </svg>
);

export function AdminCategoryContentsHeader({ adminInfo, categoryName, onBack, onLogout }) {
  const categoryLabel = String(categoryName ?? '').trim();

  return (
    <header className={styles['admin-header']} role="banner">
      <div className={styles['header-left']}>
        <BrandLogo decorative decoding="async" to="/admin-dashboard" />
        <div className={styles['header-info']}>
          <h1 className={styles['page-title']}>Category Quick Richuels</h1>
          <p className={styles['page-description']}>
            {categoryLabel
              ? `Manage cover images, videos, and action items for ${categoryLabel}`
              : 'Manage cover images, videos, and action items for this category'}
          </p>
        </div>
      </div>
      <div className={styles['header-right']}>
        <div className={styles['admin-info']}>
          <div className={styles['admin-details']}>
            <p className={styles['admin-name']}>{adminInfo.fullName}</p>
            <span className={styles['admin-details-sep']} aria-hidden="true" />
            <p className={styles['admin-email']}>{adminInfo.email}</p>
          </div>
          <button type="button" className={styles['users-button']} onClick={onBack} aria-label="Back to categories">
            <BackIcon />
            Categories
          </button>
          <button type="button" className={styles['logout-button']} onClick={onLogout}>
            <LogoutIcon />
            Log out
          </button>
        </div>
      </div>
    </header>
  );
}
